import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { CATEGORIES } from '../data/categories';
import { Award, BadgeCheck, Factory, MapPin, Phone, Mail, ShieldCheck, Users, Wrench, ArrowRight } from 'lucide-react';

const CREDENTIALS = [
  { icon: Factory, title: 'Registered Indian Company', desc: 'Harpalyk Engineering Private Limited, incorporated in 2024, headquartered in Gurugram, Haryana.' },
  { icon: BadgeCheck, title: 'Certified Products', desc: 'Products and accessories carry CE & RoHS certifications with rigorous pre-delivery quality checks.' },
  { icon: ShieldCheck, title: '2-Year Warranty', desc: 'Comprehensive warranty with genuine spare parts and a documented warranty policy.' },
  { icon: Wrench, title: 'Installation & Support', desc: 'Pan-India delivery, professional installation and after-sales service network across 25+ cities.' },
  { icon: Users, title: 'Trusted by Leading Brands', desc: 'Deployed at properties served by Sodexo, Grand Hyatt and Park Hyatt, plus 500+ homes.' },
  { icon: Award, title: '4.8/5 Customer Rating', desc: 'Rated 4.8 out of 5 across 500+ verified customer reviews.' },
];

export default function AboutUsPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    window.scrollTo(0, 0);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Harpalyk Engineering',
    url: 'https://harpalyk.com/about-us',
    mainEntity: {
      '@type': 'Organization',
      name: 'Harpalyk Engineering Private Limited',
      foundingDate: '2024',
      url: 'https://harpalyk.com/',
      telephone: '+91-7011370322',
      email: 'sales@harpalyk.com',
      address: { '@type': 'PostalAddress', addressLocality: 'Gurugram', addressRegion: 'Haryana', addressCountry: 'IN' },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About Harpalyk Engineering | Premium Cleaning Robots Company India</title>
        <meta name="description" content="Harpalyk Engineering Pvt. Ltd. (founded 2024, Gurugram) builds premium AI-powered cleaning robots for Indian homes, hotels & commercial buildings. CE & RoHS certified, 2-year warranty, pan-India support." />
        <link rel="canonical" href="https://harpalyk.com/about-us" />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>
      <Header scrolled={scrolled} />
      <main className="pt-16 lg:pt-[72px]" data-testid="about-us-page">
        <Breadcrumbs items={[{ label: 'About Us' }]} />

        <section className="py-16 lg:py-24 px-6 lg:px-12 bg-gradient-to-b from-[#F8F9FA] to-white">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-semibold tracking-[0.25em] text-emerald-600 uppercase mb-4">About Harpalyk</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 mb-8">
              India's Premium Home Robotics Company
            </h1>
            <div className="space-y-5 text-base md:text-lg text-gray-600 leading-relaxed">
              <p>
                <strong className="text-gray-900">Harpalyk Engineering Private Limited</strong> was founded in 2024 in Gurugram, Haryana, with one mission: bring world-class robotic cleaning automation to Indian homes, hotels, hospitals and commercial buildings — engineered for Indian dust, Indian voltages and Indian conditions.
              </p>
              <p>
                Today we design and deliver four categories of intelligent robots — <Link to="/floor-cleaning-robots" className="text-emerald-700 font-medium hover:underline">floor cleaning robots</Link>, <Link to="/pool-cleaning-robots" className="text-emerald-700 font-medium hover:underline">pool cleaning robots</Link>, <Link to="/window-cleaning-robots" className="text-emerald-700 font-medium hover:underline">window cleaning robots</Link> and <Link to="/robotic-lawn-mowers" className="text-emerald-700 font-medium hover:underline">robotic lawn mowers</Link> — backed by pan-India delivery, professional installation and a 2-year warranty.
              </p>
              <p>
                Our robots serve everyone from families and villa owners to facility teams at hotels, resorts and corporate campuses. We believe robots should be partners that give you back your time — reliable, safe and effortless.
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 lg:py-20 px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-10">Business Credentials & Trust</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="credentials-grid">
              {CREDENTIALS.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="rounded-2xl border border-black/5 bg-[#F8F9FA] p-6 hover:bg-white hover:shadow-lg transition-[box-shadow,background-color] duration-300">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-base font-semibold text-gray-900 mb-1.5">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 lg:py-20 px-6 lg:px-12 bg-[#F8F9FA]">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-gray-900 mb-10">Our Robot Categories</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {CATEGORIES.map((cat) => (
                <Link key={cat.id} to={cat.path} className="group rounded-2xl border border-black/5 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
                  <div className="h-32 rounded-xl overflow-hidden bg-[#F8F9FA] mb-4">
                    <img src={cat.image} alt={cat.name} loading="lazy" className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-gray-900 mb-1">{cat.name}</h3>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                    Explore <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 lg:py-20 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto rounded-3xl border border-black/5 bg-[#0A0A0A] p-10 lg:p-14 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">Talk to Our Team</h2>
            <p className="text-gray-400 mb-8">Questions about products, dealership or support? We respond within hours.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-gray-300 mb-8">
              <span className="inline-flex items-center gap-2"><Phone size={16} className="text-emerald-400" /> +91-7011370322</span>
              <span className="inline-flex items-center gap-2"><Mail size={16} className="text-emerald-400" /> sales@harpalyk.com</span>
              <span className="inline-flex items-center gap-2"><MapPin size={16} className="text-emerald-400" /> Gurugram, Haryana, India</span>
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="/#contact" data-testid="about-contact-cta" className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-gray-900 px-8 py-3.5 text-sm font-medium hover:bg-emerald-50 transition-colors duration-300">
                Book a Demo <ArrowRight size={16} />
              </a>
              <Link to="/dealer" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white px-8 py-3.5 text-sm font-medium hover:border-white transition-colors duration-300">
                Become a Dealer
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppWidget />
    </div>
  );
}
